import { computed, ref } from 'vue'

import { FORBIDDEN, UNAUTHORIZED } from '~/constants/http'
import { getNewToken } from '~/utils/auth'
import { HttpClientProxy } from '~/utils/http'

import { useNuxtApp, useRuntimeConfig } from '#app'
import type { TCommon, TGeneral } from '~/src/types/common'
import type { TBrochureFile } from '~/src/types/download'
import type { TReport } from '~/src/types/reports'
import { downloadFile } from '~/src/utils/download'
import { extractFilenameFromHeaders } from '~/src/utils/http'
import { addWatermarkInPDF } from '~/src/utils/pdf'
import { getVimeoData } from '~/src/utils/vimeo'
import type { TDownloadFile } from '~/types/benchmark-app.type'
import { logError } from '~/utils/log-error'

export function useDownloadReport() {
  const downloading = ref(false)
  const downloadProgress = ref(0)
  const retries = ref(0)
  const abortController = ref<AbortController>()
  const { $cookies, $axios, $gtag, $membershipApi, $rudderstack }: TGeneral =
    useNuxtApp()

  const progressBarDownloadingStyles = computed(() => ({
    width: `${downloadProgress.value > 5 ? downloadProgress.value : 0}%`,
  }))
  const apiRestEndpoint = useRuntimeConfig().public.apiRestEndpoint

  const resetDownloadState = () => {
    downloading.value = false
    downloadProgress.value = 0
    abortController.value = undefined
  }

  const cancelDownload = () => {
    if (!abortController.value) return

    abortController.value.abort()
    resetDownloadState()
  }

  const openPDFInNewTab = async (id: string, category: string) => {
    $rudderstack.trackDownloads({
      file_id: id,
      file_name: 'Dynamic report opened in a new tab by id',
    })
    const url = `${apiRestEndpoint}/downloads/category/${category}/${id}`
    await downloadReportRequest(url, null, null, null, null, true)
  }

  const downloadReportRequest = async (
    url: string,
    filename = null,
    onErrorCallback = null,
    category = null,
    isMultiple = false,
    openInNewTab = false,
  ) => {
    if (downloading.value) return

    downloading.value = !isMultiple

    const options = await HttpClientProxy.buildHttpOptions(
      {},
      $cookies,
      useRuntimeConfig(),
    )
    abortController.value = new AbortController()

    await $axios({
      method: 'POST',
      url,
      responseType: 'blob',
      headers: options.headers,
      signal: abortController.value.signal,
      onDownloadProgress: (progressEvent: TCommon) => {
        const total = parseFloat(progressEvent.total?.toString() || '0')
        const current = progressEvent.loaded
        downloadProgress.value = Math.floor((current / total) * 100)
      },
    })
      .then(async ({ data: blob, headers }) => {
        retries.value = 0
        const isPDF = extractFilenameFromHeaders(headers)?.endsWith('.pdf')
        let blobFile = blob
        if (isPDF && category !== 'public-documents') {
          const pdfWithWatermark = await addWatermarkInPDF(blob)
          blobFile = new Blob([pdfWithWatermark], {
            type: 'application/pdf',
          })
        }

        const blobUrl = window.URL.createObjectURL(blobFile)
        if (openInNewTab) {
          window.open(blobUrl, '_blank')
          return
        }

        downloadFile({
          url: blobUrl,
          filename: extractFilenameFromHeaders(headers),
        })
        window.URL.revokeObjectURL(blobUrl)

        $gtag.event('ce_file_download', {
          event_category: 'download',
          value: extractFilenameFromHeaders(headers),
          send_to: 'default',
        })
      })
      .catch(async (e: TCommon) => {
        if (e.status === FORBIDDEN || e.response?.status === FORBIDDEN) {
          await $membershipApi.getActiveMemberships()

          if (onErrorCallback && typeof onErrorCallback === 'function') {
            onErrorCallback()
          }
        }

        if (e.response?.status === UNAUTHORIZED && retries.value < 1) {
          await getNewToken({
            $cookies: $cookies,
            $config: useRuntimeConfig(),
          })
          downloading.value = false
          retries.value++
          await downloadReportRequest(url, filename, onErrorCallback)
        }
      })
      .finally(() => {
        resetDownloadState()
      })
  }

  const downloadReport = async (
    report: TReport | TBrochureFile | TDownloadFile,
    category: string,
    onErrorCallback = null,
  ): Promise<void> => {
    try {
      if (!report.file_name) return
      const id = (report as TReport).id || report.ID

      $rudderstack.trackDownloads({
        file_id: id,
        file_name: report.file_name,
        file_type: (report as TReport)?.file_extension,
      })

      await downloadReportRequest(
        `${apiRestEndpoint}/downloads/category/${category}/${id}`,
        report.file_name,
        onErrorCallback,
        category,
      )
    } catch (error) {
      logError(error)
    }
  }

  const downloadReportById = async (
    id: string | number,
    onErrorCallback = null,
  ): Promise<void> => {
    $rudderstack.trackDownloads({
      file_id: id,
      file_name: 'Dynamic report download by id',
    })

    await downloadReportRequest(
      `${apiRestEndpoint}/downloads/report/${id}`,
      null,
      onErrorCallback,
    )
  }

  const downloadReportByIds = async (ids: string[], onErrorCallback = null) => {
    await Promise.all(
      ids.map(async (id) => {
        await downloadReportRequest(
          `${apiRestEndpoint}/downloads/report/${id}`,
          null,
          onErrorCallback,
          null,
          true,
        )
      }),
    )
  }

  const downloadVideoVimeo = async (vimeoId: string) => {
    const { $rudderstack } = useNuxtApp()

    try {
      downloading.value = true
      const data = await getVimeoData({
        vimeoId,
        downloadProgress,
      })
      const downloadUrl = data.download.find(
        (download: TCommon) =>
          download.quality === 'hd' && download.rendition === '720p',
      )?.link
      if (downloadUrl) {
        $rudderstack.trackDownloads({
          file_id: vimeoId,
          file_name: data.name,
          file_type: 'mp4',
        })
        downloadFile({ url: downloadUrl })
      }
    } catch (_error) {
      logError(_error)
    } finally {
      resetDownloadState()
    }
  }

  return {
    downloading,
    downloadProgress,
    progressBarDownloadingStyles,
    abortController,
    downloadReport,
    downloadReportRequest,
    addWatermarkInPDF,
    getVimeoData,
    downloadVideoVimeo,
    resetDownloadState,
    cancelDownload,
    downloadReportById,
    downloadReportByIds,
    openPDFInNewTab,
  }
}
